import { useState } from 'react';
import { Surface, Button, Badge, Input, Select, Tabs, Modal, Checkbox, StatCard, Grid, PageHeader, Alert } from '../../ui/components';
import { Search, RefreshCw, Gavel, Eye, Send } from 'lucide-react';
import FloatingOwenAssistant from '../../components/common/FloatingOwenAssistant';
import FloatingKnowledge from '../../components/common/FloatingKnowledge';

const mockMarketplace = [
  {
    id: '1',
    anonymous_id: 'LK-A1B2C3D4',
    product_short_name: 'Conventional',
    property_state: 'IL',
    property_city: 'Springfield',
    property_type: 'Single Family',
    property_use: 'Primary Residence',
    loan_amount: 388000,
    purchase_price: 485000,
    down_payment: 97000,
    ltv_ratio: 80,
    dti_ratio: 28.5,
    credit_score: 742,
    readiness_score: 84,
    income_monthly: 9200,
    total_assets: 250000,
    bid_count: 2,
    marketplace_listed_at: new Date(Date.now() - 5 * 86400000).toISOString(),
  },
  {
    id: '4',
    anonymous_id: 'LK-M3N4O5P6',
    product_short_name: 'Jumbo',
    property_state: 'WA',
    property_city: 'Seattle',
    property_type: 'Single Family',
    property_use: 'Primary Residence',
    loan_amount: 937500,
    purchase_price: 1250000,
    down_payment: 312500,
    ltv_ratio: 75,
    dti_ratio: 18.4,
    credit_score: 795,
    readiness_score: 95,
    income_monthly: 18500,
    total_assets: 1245000,
    bid_count: 2,
    marketplace_listed_at: new Date(Date.now() - 3 * 86400000).toISOString(),
  },
  {
    id: '7',
    anonymous_id: 'LK-Y5Z6A7B8',
    product_short_name: 'Conventional',
    property_state: 'GA',
    property_city: 'Atlanta',
    property_type: 'Single Family',
    property_use: 'Primary Residence',
    loan_amount: 304000,
    purchase_price: 380000,
    down_payment: 76000,
    ltv_ratio: 80,
    dti_ratio: 19.2,
    credit_score: 758,
    readiness_score: 87,
    income_monthly: 14500,
    total_assets: 532000,
    bid_count: 3,
    marketplace_listed_at: new Date(Date.now() - 2 * 86400000).toISOString(),
  },
  {
    id: '5',
    anonymous_id: 'LK-Q7R8S9T0',
    product_short_name: 'Conventional',
    property_state: 'CO',
    property_city: 'Denver',
    property_type: 'Townhouse',
    property_use: 'Primary Residence',
    loan_amount: 332000,
    purchase_price: 415000,
    down_payment: 83000,
    ltv_ratio: 80,
    dti_ratio: 24.8,
    credit_score: 721,
    readiness_score: 78,
    income_monthly: 11200,
    total_assets: 295000,
    bid_count: 1,
    marketplace_listed_at: new Date(Date.now() - 8 * 86400000).toISOString(),
  },
  {
    id: '3',
    anonymous_id: 'LK-I9J0K1L2',
    product_short_name: 'VA',
    property_state: 'CA',
    property_city: 'San Diego',
    property_type: 'Single Family',
    property_use: 'Primary Residence',
    loan_amount: 625000,
    purchase_price: 625000,
    down_payment: 0,
    ltv_ratio: 100,
    dti_ratio: 22.1,
    credit_score: 768,
    readiness_score: 91,
    income_monthly: 8500,
    total_assets: 635000,
    bid_count: 0,
    marketplace_listed_at: new Date(Date.now() - 1 * 86400000).toISOString(),
  },
];

const mockMyBids = [
  { id: 'b1', anonymous_id: 'LK-A1B2C3D4', loan_amount: 388000, product: 'Conventional', interest_rate: 6.625, status: 'pending', created_at: new Date(Date.now() - 4 * 86400000).toISOString() },
  { id: 'b3', anonymous_id: 'LK-M3N4O5P6', loan_amount: 937500, product: 'Jumbo', interest_rate: 6.500, status: 'viewed', created_at: new Date(Date.now() - 2 * 86400000).toISOString() },
  { id: 'b7', anonymous_id: 'LK-Y5Z6A7B8', loan_amount: 304000, product: 'Conventional', interest_rate: 6.625, status: 'accepted', created_at: new Date(Date.now() - 1 * 86400000).toISOString() },
  { id: 'b8', anonymous_id: 'LK-Q7R8S9T0', loan_amount: 332000, product: 'Conventional', interest_rate: 6.875, status: 'accepted', created_at: new Date(Date.now() - 10 * 86400000).toISOString() },
];

const LenderMarketplace = () => {
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [bidDialogOpen, setBidDialogOpen] = useState(false);
  const [bidRates, setBidRates] = useState({});
  const [marketplace] = useState(mockMarketplace);
  const [myBids] = useState(mockMyBids);
  const [loading] = useState(false);
  const [leadDetailOpen, setLeadDetailOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);

  const handleViewLead = (app) => {
    setSelectedLead(app);
    setLeadDetailOpen(true);
  };

  const handleSelectAll = (e) => {
    setSelected(e.target.checked ? marketplace.map((app) => app.id) : []);
  };

  const handleSelect = (id) => {
    setSelected((prev) => {
      const newSelected = prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id];
      if (!prev.includes(id)) setBidRates((r) => ({ ...r, [id]: 6.5 }));
      return newSelected;
    });
  };

  const handleBid = () => {
    selected.forEach((id) => { if (!bidRates[id]) setBidRates((r) => ({ ...r, [id]: 6.5 })); });
    setBidDialogOpen(true);
  };

  const handleSubmitBid = async () => {
    alert(`Placed ${selected.length} bid(s)`);
    setSelected([]);
    setBidRates({});
    setBidDialogOpen(false);
  };

  const filteredMarketplace = marketplace.filter((app) =>
    app.anonymous_id.toLowerCase().includes(search.toLowerCase()) ||
    app.property_city.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      {loading && (
        <div style={{ height: 4, background: 'var(--lk-primary-500)', animation: 'lk-progress 1.5s infinite', marginBottom: 'var(--lk-space-2)' }} />
      )}

      {/* Stats Cards */}
      <Grid cols={4} gap="var(--lk-space-4)" style={{ marginBottom: 'var(--lk-space-4)' }}>
        <StatCard label="Total Leads" value={marketplace.length} />
        <StatCard label="Pending Bids" value={myBids.filter((b) => b.status === 'pending').length} />
        <StatCard label="In Progress" value={myBids.filter((b) => b.status === 'accepted').length} />
        <StatCard label="Unassigned" value={marketplace.filter((a) => a.bid_count === 0).length} />
      </Grid>

      {/* Main Card with Tabs */}
      <Surface padding="0">
        <div style={{ padding: 'var(--lk-space-4) var(--lk-space-5) 0' }}>
          <Tabs
            tabs={[
              { id: 0, label: `Marketplace (${marketplace.length})` },
              { id: 1, label: `My Bids (${myBids.length})` },
            ]}
            activeTab={tab}
            onChange={setTab}
          />
        </div>

        <div style={{ padding: 'var(--lk-space-4) var(--lk-space-5) var(--lk-space-5)' }}>
          {tab === 0 && (
            <>
              <div style={{ display: 'flex', gap: 'var(--lk-space-2)', marginBottom: 'var(--lk-space-3)', alignItems: 'center' }}>
                <div style={{ width: 300 }}>
                  <Input
                    placeholder="Search..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <Button variant="secondary" icon={RefreshCw} size="sm">Refresh</Button>
                <div style={{ flex: 1 }} />
                {selected.length > 0 && (
                  <Button variant="primary" icon={Gavel} onClick={handleBid}>
                    Bid on {selected.length} Lead{selected.length > 1 ? 's' : ''}
                  </Button>
                )}
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table className="lk-table">
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}>
                        <input
                          type="checkbox"
                          checked={selected.length === marketplace.length && marketplace.length > 0}
                          onChange={handleSelectAll}
                        />
                      </th>
                      <th>Lead ID</th>
                      <th>Location</th>
                      <th>Product</th>
                      <th style={{ textAlign: 'right' }}>Loan Amount</th>
                      <th style={{ textAlign: 'center' }}>Credit</th>
                      <th style={{ textAlign: 'center' }}>DTI</th>
                      <th style={{ textAlign: 'center' }}>Score</th>
                      <th style={{ textAlign: 'center' }}>Status</th>
                      <th>Submitted</th>
                      <th style={{ textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMarketplace.map((app) => (
                      <tr key={app.id} style={selected.includes(app.id) ? { backgroundColor: 'var(--lk-primary-50)' } : {}}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selected.includes(app.id)}
                            onChange={() => handleSelect(app.id)}
                          />
                        </td>
                        <td>
                          <span style={{ fontFamily: 'monospace', fontWeight: 600, color: 'var(--lk-primary-600)' }}>{app.anonymous_id}</span>
                        </td>
                        <td style={{ color: 'var(--lk-text-tertiary)' }}>{app.property_city}, {app.property_state}</td>
                        <td><Badge variant="neutral">{app.product_short_name}</Badge></td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>${app.loan_amount.toLocaleString()}</td>
                        <td style={{ textAlign: 'center', fontWeight: 600 }}>{app.credit_score}</td>
                        <td style={{ textAlign: 'center', color: 'var(--lk-text-tertiary)' }}>{app.dti_ratio}%</td>
                        <td style={{ textAlign: 'center' }}>
                          <Badge variant={app.readiness_score >= 80 ? 'success' : 'primary'}>
                            {app.readiness_score}
                          </Badge>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <Badge variant="primary">In Marketplace</Badge>
                        </td>
                        <td style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)' }}>{new Date(app.marketplace_listed_at).toLocaleDateString()}</td>
                        <td style={{ textAlign: 'center' }}>
                          <Button size="sm" variant="ghost" icon={Eye} onClick={() => handleViewLead(app)} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {tab === 1 && (
            <div style={{ overflowX: 'auto' }}>
              <table className="lk-table">
                <thead>
                  <tr>
                    <th>Lead ID</th>
                    <th>Product</th>
                    <th style={{ textAlign: 'right' }}>Loan Amount</th>
                    <th style={{ textAlign: 'center' }}>My Rate</th>
                    <th>Status</th>
                    <th>Bid Date</th>
                  </tr>
                </thead>
                <tbody>
                  {myBids.map((bid) => (
                    <tr key={bid.id}>
                      <td>
                        <span style={{ fontFamily: 'monospace', fontWeight: 600, color: 'var(--lk-primary-600)' }}>{bid.anonymous_id}</span>
                      </td>
                      <td><Badge variant="neutral">{bid.product || '-'}</Badge></td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>${bid.loan_amount.toLocaleString()}</td>
                      <td style={{ textAlign: 'center', fontWeight: 700, color: 'var(--lk-primary-600)' }}>{Number(bid.interest_rate).toFixed(3)}%</td>
                      <td>
                        <Badge variant={bid.status === 'accepted' ? 'success' : bid.status === 'viewed' ? 'primary' : 'warning'}>
                          {bid.status}
                        </Badge>
                      </td>
                      <td style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)' }}>{new Date(bid.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Surface>

      {/* Bid Dialog */}
      <Modal open={bidDialogOpen} onClose={() => setBidDialogOpen(false)} title={`Place Bids on ${selected.length} Lead(s)`} size="lg">
        <Alert variant="info" style={{ marginBottom: 'var(--lk-space-3)' }}>
          Set an individual interest rate for each lead (3.0% - 10.0%).
        </Alert>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lk-space-3)' }}>
          {marketplace.filter((app) => selected.includes(app.id)).map((app) => (
            <Surface key={app.id} padding="var(--lk-space-3)">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontFamily: 'monospace', fontWeight: 600 }}>{app.anonymous_id}</div>
                  <div style={{ display: 'flex', gap: 'var(--lk-space-2)', marginTop: 4 }}>
                    <Badge variant="neutral">{app.product_short_name}</Badge>
                    <Badge variant="neutral">${app.loan_amount.toLocaleString()}</Badge>
                  </div>
                </div>
                <div style={{ width: 120 }}>
                  <Input
                    label="Rate %"
                    type="number"
                    value={bidRates[app.id] || 6.5}
                    onChange={(e) => setBidRates((r) => ({ ...r, [app.id]: parseFloat(e.target.value) }))}
                    min={3}
                    max={10}
                    step={0.125}
                  />
                </div>
              </div>
            </Surface>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--lk-space-2)', marginTop: 'var(--lk-space-4)' }}>
          <Button variant="secondary" onClick={() => setBidDialogOpen(false)}>Cancel</Button>
          <Button variant="primary" icon={Send} onClick={handleSubmitBid}>Submit Bids</Button>
        </div>
      </Modal>

      {/* Lead Detail Modal */}
      <Modal open={leadDetailOpen} onClose={() => setLeadDetailOpen(false)} title="Lead Details" size="lg">
        {selectedLead && (
          <>
            <Grid cols={2} gap="var(--lk-space-4)" style={{ marginBottom: 'var(--lk-space-4)' }}>
              <Surface padding="var(--lk-space-4)">
                <div style={{ fontWeight: 600, marginBottom: 'var(--lk-space-3)' }}>Loan Summary</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {[['Lead ID', selectedLead.anonymous_id], ['Product', selectedLead.product_short_name], ['Loan Amount', `$${selectedLead.loan_amount.toLocaleString()}`], ['Purchase Price', `$${selectedLead.purchase_price.toLocaleString()}`], ['Down Payment', `$${selectedLead.down_payment.toLocaleString()} (${Math.round(selectedLead.down_payment / selectedLead.purchase_price * 100)}%)`], ['LTV', `${selectedLead.ltv_ratio}%`]].map(([label, value]) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--lk-border-subtle)' }}>
                      <span style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)' }}>{label}</span>
                      <span style={{ fontWeight: 500, fontSize: 'var(--lk-text-sm)' }}>{value}</span>
                    </div>
                  ))}
                </div>
              </Surface>
              <Surface padding="var(--lk-space-4)">
                <div style={{ fontWeight: 600, marginBottom: 'var(--lk-space-3)' }}>Borrower Profile</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {[['Credit Score', <Badge key="cs" variant={selectedLead.credit_score >= 740 ? 'success' : selectedLead.credit_score >= 680 ? 'warning' : 'danger'}>{selectedLead.credit_score}</Badge>], ['DTI Ratio', `${selectedLead.dti_ratio}%`], ['Monthly Income', `$${selectedLead.income_monthly?.toLocaleString() || 'N/A'}`], ['Total Assets', `$${selectedLead.total_assets?.toLocaleString() || 'N/A'}`], ['Readiness Score', <Badge key="rs" variant={selectedLead.readiness_score >= 80 ? 'success' : 'primary'}>{selectedLead.readiness_score}/100</Badge>]].map(([label, value]) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--lk-border-subtle)' }}>
                      <span style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)' }}>{label}</span>
                      <span style={{ fontWeight: 500, fontSize: 'var(--lk-text-sm)' }}>{value}</span>
                    </div>
                  ))}
                </div>
              </Surface>
            </Grid>
            <Surface padding="var(--lk-space-4)" style={{ marginBottom: 'var(--lk-space-4)' }}>
              <div style={{ fontWeight: 600, marginBottom: 'var(--lk-space-3)' }}>Property</div>
              <div style={{ display: 'flex', gap: 'var(--lk-space-4)' }}>
                <div><span style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)' }}>Location: </span><span style={{ fontWeight: 500 }}>{selectedLead.property_city}, {selectedLead.property_state}</span></div>
                <div><span style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)' }}>Type: </span><span style={{ fontWeight: 500 }}>{selectedLead.property_type}</span></div>
                <div><span style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)' }}>Use: </span><span style={{ fontWeight: 500 }}>{selectedLead.property_use}</span></div>
              </div>
            </Surface>
            <div style={{ display: 'flex', gap: 'var(--lk-space-3)', alignItems: 'center' }}>
              <div style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)' }}>Listed: {new Date(selectedLead.marketplace_listed_at).toLocaleDateString()}</div>
              <div style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)' }}>{selectedLead.bid_count} bid{selectedLead.bid_count !== 1 ? 's' : ''} placed</div>
            </div>
          </>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--lk-space-2)', marginTop: 'var(--lk-space-4)' }}>
          {selectedLead && !selected.includes(selectedLead.id) && (
            <Button variant="primary" icon={Gavel} onClick={() => { handleSelect(selectedLead.id); setLeadDetailOpen(false); handleBid(); }}>Place Bid</Button>
          )}
          <Button variant="secondary" onClick={() => setLeadDetailOpen(false)}>Close</Button>
        </div>
      </Modal>

      <FloatingOwenAssistant 
        context="lender-marketplace"
        contextData={{ selectedLeads: selected, totalBids: mockMarketplace.length }}
      />
      <FloatingKnowledge 
        customContent={{
          label: 'Marketplace',
          title: 'Bidding best practices',
          body: 'Evaluate leads carefully and place competitive bids to win quality loans.',
          bullets: [
            'Review credit scores and DTI ratios carefully',
            'Consider property location and loan amount',
            'Competitive rates win more bids',
            'Respond quickly to accepted bids',
          ],
          resources: [
            { label: 'Pricing strategies', href: '#' },
            { label: 'Market trends', href: '#' },
          ],
        }}
      />
    </div>
  );
};

export default LenderMarketplace;
